import { Fragment, useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Collapse, Input, Label, Spinner, Table } from 'reactstrap';
import { api } from '../../api/client.js';
import { formatDate, formatKickoff } from '../../utils/dates.js';

const SHARED = 'shared';

export function AdminInvites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [form, setForm] = useState({ note: '', kind: '1', expiresInDays: 14 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvites(await api.admin.invites());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create() {
    setBusy(true);
    try {
      await api.admin.createInvite({
        note: form.note || null,
        expiresInDays: Number(form.expiresInDays),
        maxUses: form.kind === SHARED ? null : Number(form.kind),
      });
      setForm({ ...form, note: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id) {
    try {
      await api.admin.revokeInvite(id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function statusOf(invite) {
    if (invite.isRevoked) return <Badge color="secondary">Revoked</Badge>;
    if (invite.isExpired) return <Badge color="warning" className="text-dark">Expired</Badge>;
    if (invite.isExhausted) return <Badge color="success">Used up</Badge>;
    return <Badge color="primary">Available</Badge>;
  }

  function usesOf(invite) {
    if (invite.maxUses == null) {
      return <>{invite.useCount} <span className="text-muted">/ unlimited</span></>;
    }
    return <>{invite.useCount} <span className="text-muted">/ {invite.maxUses}</span></>;
  }

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <div>
      <h1 className="h3 mb-3">Invite codes</h1>
      <p className="text-muted small">
        Registration is invite-only. A single-use code works once; a shared code lets
        anyone who has it register until it expires or you revoke it.
      </p>

      {error && <Alert fade={false} color="danger" toggle={() => setError(null)}>{error}</Alert>}

      <div className="d-flex gap-3 align-items-end flex-wrap mb-4 p-3 border rounded bg-body-tertiary">
        <div>
          <Label for="kind" className="mb-1 small text-muted">Type</Label>
          <Input id="kind" type="select" value={form.kind} style={{ width: 190 }}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="1">Single use</option>
            <option value="5">Up to 5 people</option>
            <option value="10">Up to 10 people</option>
            <option value={SHARED}>Shared &mdash; unlimited</option>
          </Input>
        </div>

        <div>
          <Label for="expires" className="mb-1 small text-muted">Expires in (days)</Label>
          <Input id="expires" type="number" min="1" max="365" style={{ width: 140 }}
            value={form.expiresInDays}
            onChange={(e) => setForm({ ...form, expiresInDays: e.target.value })} />
        </div>

        <div className="flex-fill" style={{ minWidth: 200, maxWidth: 320 }}>
          <Label for="note" className="mb-1 small text-muted">Note (optional)</Label>
          <Input id="note" value={form.note} placeholder="Who is this for?"
            onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>

        <Button color="primary" onClick={create} disabled={busy}>
          {busy ? <Spinner size="sm" /> : 'Generate code'}
        </Button>
      </div>

      {invites.length === 0 ? (
        <Alert fade={false} color="secondary">No invite codes yet.</Alert>
      ) : (
        <Table hover className="align-middle">
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Note</th>
              <th className="text-center">Status</th>
              <th className="text-center">Registered</th>
              <th>Expires</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invites.map((invite) => (
              <Fragment key={invite.id}>
                <tr>
                  <td><code className="fs-6">{invite.code}</code></td>
                  <td className="small">
                    {invite.maxUses == null
                      ? <Badge color="info" className="text-dark">Shared</Badge>
                      : invite.maxUses === 1
                        ? <span className="text-muted">Single use</span>
                        : <Badge color="info" className="text-dark">Up to {invite.maxUses}</Badge>}
                  </td>
                  <td className="text-muted small">{invite.note ?? '\u2014'}</td>
                  <td className="text-center">{statusOf(invite)}</td>
                  <td className="text-center">
                    {usesOf(invite)}
                    {invite.useCount > 0 && (
                      <Button size="sm" color="link" className="p-0 ms-2 align-baseline"
                        onClick={() => setExpanded(expanded === invite.id ? null : invite.id)}>
                        {expanded === invite.id ? 'hide' : 'who?'}
                      </Button>
                    )}
                  </td>
                  <td className="text-muted small">{formatDate(invite.expiresAtUtc)}</td>
                  <td className="text-end">
                    {invite.isUsable && (
                      <Button size="sm" color="link" className="text-danger"
                        onClick={() => revoke(invite.id)}>Revoke</Button>
                    )}
                  </td>
                </tr>
                <tr className="border-0">
                  <td colSpan={7} className="p-0 border-0">
                    <Collapse isOpen={expanded === invite.id}>
                      <ul className="list-unstyled small text-muted mb-0 ps-4 py-2">
                        {invite.redemptions.map((r) => (
                          <li key={`${r.email}-${r.redeemedAtUtc}`}>
                            {r.displayName} &lt;{r.email}&gt;
                            <span className="ms-2">{formatKickoff(r.redeemedAtUtc)}</span>
                          </li>
                        ))}
                      </ul>
                    </Collapse>
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
